/**
 * 定制化数字人管理器
 */
import { useEffect, useRef, useState } from "react";
import { upload, type UploadFileProps } from "@/services/upload";
import { cosUploadAvatarFile } from "@/services/cos-upload-avatar-file";
import { useToast } from "@/hooks/use-toast";
import { customAudioManager } from './custom-audio-manager';
import { canUseWindow } from "@/utils/canuseWindow";
import { imageToVideo, ImageToVideoProps } from '@/services/image-to-video';
import { videoToDigitalHuman, VideoToDigitalHumanProps } from "@/services/video-to-digital-human";
import { checkActorNameDuplicate } from "@/services/users-check-actor-name-duplicate";
import { actorNameSuffixId } from "@/services/users-actor-name-suffix-id";
import { assetsActorManager } from "./assets-actor-manager";
import { iamAccountManager } from "./iam-account-manager";
import analytics from "@/utils/analytics";
import { useMatch, useParams } from "react-router-dom";
import { userManagementManager } from "./agent-dashboard-user-management-manager";
import { SPEND_CREDITS_EVENT, subscriptionHook, subscriptionManager, } from "./subscription-manager";

// 自定义事件类型
type ActorNameChangeEventData = { actorName: string };
type ActorNameChangeEvent = CustomEvent<ActorNameChangeEventData>;

type AudioUrlChangeEventData = { audioUrl: string };
type AudioUrlChangeEvent = CustomEvent<AudioUrlChangeEventData>;

type AudioNameChangeEventData = { audioName: string };
type AudioNameChangeEvent = CustomEvent<AudioNameChangeEventData>;

class CustomDigitalHumanManager {
  private static instance: CustomDigitalHumanManager;
  // 自定义事件名称
  private readonly STORAGE_KEY = "MIUTA_CUSTOM_DIGITAL_HUMAN";
  // 生成类型
  private generateType: 'image' | 'video' = 'image';
  // 上传后的文件地址
  private fileUri: string = '';
  // 数字人名称
  private actorName: string = 'Actor';
  // 音频url
  private audioUrl: string = '';
  // 音频名称
  private audioName: string = '';

  private constructor() { }

  // 单例模式
  public static getInstance(): CustomDigitalHumanManager {
    if (!CustomDigitalHumanManager.instance) {
      CustomDigitalHumanManager.instance = new CustomDigitalHumanManager();
    }
    return CustomDigitalHumanManager.instance;
  }

  /** 初始化 */
  public init({
    fileUri,
    actorName,
    audioUrl,
    audioName,
  }: {
    fileUri?: string;
    actorName?: string;
    audioUrl?: string;
    audioName?: string;
  }) {
    fileUri && this.setFileUri(fileUri);
    this.setAudioUrl(audioUrl);
    this.setAudioName(audioName);
    this.setActorName(actorName);
  }

  public setGenerateType(type: 'image' | 'video') {
    this.generateType = type;
  }
  public getGenerateType() {
    return this.generateType;
  }

  public getFileUri(): string {
    return this.fileUri;
  }
  public setFileUri(fileUri: string) {
    this.fileUri = fileUri;
  }

  public setActorName(actorName: string) {
    this.actorName = actorName;
    this.dispatchNameChange(actorName);
  }
  public getActorName(): string {
    return this.actorName;
  }

  public getAudioUrl(): string {
    return this.audioUrl;
  }
  public setAudioUrl(audioUrl: string) {
    this.audioUrl = audioUrl;
    this.dispatchAudioUrlChange(audioUrl);
  }

  public getAudioName(): string {
    return this.audioName;
  }
  public setAudioName(audioName: string) {
    this.audioName = audioName;
    this.dispatchAudioNameChange(audioName);
  }

  // 上传文件 - 图片/视频（废弃）
  public async uploadFile({ file, onProgress }: UploadFileProps) {
    const res = await upload({ file, onProgress });
    this.setFileUri(res.fileUri); // 文件地址
    return res;
  }

  /** 上传文件 - 图片/视频（COS 直传，actor 场景 ） */
  public async cosUploadFile({ file, onProgress }: UploadFileProps) {
    const res = await cosUploadAvatarFile(file, { onProgress });
    this.setFileUri(res.url);
    return res;
  }

  /** 图生视频并生成数字人 */
  public async imageToVideo({ userId, actorName, actorPic, audioName, sampleAudioUrl, point }: ImageToVideoProps) {
    const res = await imageToVideo({
      userId,
      actorName,
      actorPic,
      audioName,
      sampleAudioUrl,
      point,
    });
    return res;
  }

  /** 视频直接生成数字人 */
  public async videoToDigitalHuman({ userId, actorName, actorVideo, audioName, sampleAudioUrl }: VideoToDigitalHumanProps) {
    const res = await videoToDigitalHuman({
      userId,
      actorName,
      actorVideo,
      audioName,
      sampleAudioUrl,
    });
    return res;
  }

  /** 创建数字人 */
  public async createDigitalHuman(uploadType: 'image' | 'video', userId?: string, point?: number) {
    if (uploadType === 'image') {
      return await this.imageToVideo({
        userId,
        actorName: this.getActorName(),
        actorPic: this.getFileUri(),
        audioName: this.getAudioName(),
        sampleAudioUrl: this.getAudioUrl(),
        point,
      });
    } else if (uploadType === 'video') {
      return await this.videoToDigitalHuman({
        userId,
        actorName: this.getActorName(),
        actorVideo: this.getFileUri(),
        audioName: this.getAudioName(),
        sampleAudioUrl: this.getAudioUrl(),
      });
    }
  }

  /** 检查演员名称是否重复 */
  public async checkActorNameDuplicate(userId?: string) {
    const res = await checkActorNameDuplicate({ name: this.getActorName(), userId });
    return res;
  }

  /** 获取演员名称次数 */
  public async getActorNameSuffixId(userId?: string) {
    const res = await actorNameSuffixId({ userId });
    return res.suffixId;
  }

  public reset() {
    this.fileUri = '';
  }

  public onActorNameChange(listener: (e: ActorNameChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:actorName`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:actorName`, listener);
      };
    }
  }

  private dispatchNameChange(actorName: string) {
    if (canUseWindow()) {
      const event = new CustomEvent<ActorNameChangeEventData>(
        `${this.STORAGE_KEY}:actorName`,
        { detail: { actorName } }
      );
      window.dispatchEvent(event);
    }
  }

  public onAudioUrlChange(listener: (e: AudioUrlChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:audioUrl`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:audioUrl`, listener);
      };
    }
  }

  private dispatchAudioUrlChange(audioUrl: string) {
    if (canUseWindow()) {
      const event = new CustomEvent<AudioUrlChangeEventData>(
        `${this.STORAGE_KEY}:audioUrl`,
        { detail: { audioUrl } }
      );
      window.dispatchEvent(event);
    }
  }

  public onAudioNameChange(listener: (e: AudioNameChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:audioName`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:audioName`, listener);
      };
    }
  }

  private dispatchAudioNameChange(audioName: string) {
    if (canUseWindow()) {
      const event = new CustomEvent<AudioNameChangeEventData>(
        `${this.STORAGE_KEY}:audioName`,
        { detail: { audioName } }
      );
      window.dispatchEvent(event);
    }
  }

}

export const useCustomDigitalHuman = (uploadType: 'image' | 'video') => {
  const [name, setName] = useState("");
  const [voiceName, setVoiceName] = useState("");
  const [voiceURL, setVoiceURL] = useState("");
  const [message, setMessage] = useState("");
  const [isAllowCreate, setIsAllowCreate] = useState(false);
  const initialSuffixId = useRef<boolean>(false);
  // 防抖引用
  const setIsAllowCreateDebounceRef = useRef<number | null>(null);
  const toast = useToast();
  const params = useParams();
  const isAgentUserManagementDetail = useMatch("/agent/dashboard/user-management/:id/:userId");
  const [creating, setCreating] = useState(false);

  // 在用户管理详情页，使用 userId 作为参数
  const userId = new URLSearchParams(window.location.search).get('userId');

  // 初始化
  const init = async (params: {
    fileUri?: string;
    actorName?: string;
    audioUrl?: string;
    audioName?: string;
  }) => {
    if (!params.actorName && !initialSuffixId.current) {
      // 初始化没有 actorName，则请求接口获取次数
      const suffixId = await customDigitalHumanManager.getActorNameSuffixId(userId);
      params.actorName = `Actor ${suffixId}`;
      initialSuffixId.current = true;
    }
    customDigitalHumanManager.init(params);

    // 初始化时，检查演员名称是否重复
    setIsAllowCreate(await checkNameDuplicate(userId));
  }

  // 检查演员名称是否重复
  const checkName = async () => {
    setIsAllowCreateDebounceRef.current = window.setTimeout(async () => {
      setIsAllowCreate(await checkNameDuplicate(userId));
    }, 1000);
  };

  // 演员名称
  const setActorName = (name: string) => {
    // 清除之前的防抖定时器
    if (setIsAllowCreateDebounceRef.current) {
      clearTimeout(setIsAllowCreateDebounceRef.current);
    }
    customDigitalHumanManager.setActorName(name);
    setIsAllowCreate(false);
    setMessage("");
    checkName();
  }

  // 音频名称
  const setAudioName = (name: string) => {
    customDigitalHumanManager.setAudioName(name);
  }

  // 音频url
  const setAudioUrl = (url: string) => {
    customDigitalHumanManager.setAudioUrl(url);
  }

  /** 上传文件 */
  const upload = async (params: UploadFileProps) => {
    // 埋点属性
    const trackParams = {
      assetType: customDigitalHumanManager.getGenerateType(),
      fileSize: params.file.size,
      format: params.file.type,
      userId: iamAccountManager.get()?.id,
    }
    // 上传前埋点
    analytics.trackUploadAsset(trackParams)
    try {
      const res = await customDigitalHumanManager.cosUploadFile(params);
      // 上传成功埋点
      analytics.trackUploadAssetSuccess(trackParams)
      return res;
    } catch (error) {
      // 上传失败埋点
      analytics.trackUploadAssetFail(trackParams)
      toast.showToastByCode(error);
      throw new Error(error)
    }
  };

  /** 创建数字人 */
  const createActor = async (uploadType: 'image' | 'video', point?: number) => {
    // 在用户管理详情页，使用 userId 作为参数
    const userId = new URLSearchParams(window.location.search).get('userId');

    // 创建前埋点
    const trackParams = {
      user_id: iamAccountManager.get()?.id,
      source: uploadType,
    }
    analytics.trackTrainDigitalHuman(trackParams)
    try {
      setCreating(true);
      setIsAllowCreate(false);
      await customDigitalHumanManager.createDigitalHuman(
        uploadType,
        // 在智能体用户详情中创建数字人时，需要传递 userId
        isAgentUserManagementDetail ? userId : undefined,
        point
      );
      assetsActorManager.fetch();

      // 在智能体用户详情中创建数字人时，需要刷新用户详情中的数字人列表
      if (isAgentUserManagementDetail && userId) {
        await userManagementManager.fetchAgentUserActors();
      }
      // 更新积分
      subscriptionManager.fetchAgentCredits();
      // 积分消耗埋点
      // analytics.trackCreditConsumed({
      //   user_email: iamAccountManager.get()?.email,
      //   credits_used: spendCredits,
      //   remaining_balance: remainCredits,
      //   feature_name: uploadType === 'image'
      //     ? SPEND_CREDITS_EVENT.image_number_person
      //     : SPEND_CREDITS_EVENT.video_number_person,
      // })
      return;
    } catch (error) {
      toast.showToastByCode(error);
      throw new Error(error)
    } finally {
      setIsAllowCreate(true);
      setCreating(false);
    }
  }

  /** 检查演员名称是否重复 */
  const checkNameDuplicate = async (userId?: string) => {
    if (!customDigitalHumanManager.getActorName()) return;
    try {
      const res = await customDigitalHumanManager.checkActorNameDuplicate(userId);
      if (res.isDuplicate) {
        setMessage("Actor name already exists");
        return false
      }
      return true;
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    }
  }

  useEffect(() => {
    return customDigitalHumanManager.onActorNameChange((e) => {
      setName(e.detail.actorName);
    });
  }, []);

  useEffect(() => {
    return customDigitalHumanManager.onAudioNameChange((e) => {
      setVoiceName(e.detail.audioName);
    });
  }, []);

  useEffect(() => {
    return customDigitalHumanManager.onAudioUrlChange((e) => {
      setVoiceURL(e.detail.audioUrl);
    });
  }, []);

  return {
    name,
    message,
    voiceName,
    voiceURL,
    isAllowCreate,
    creating,
    isAgentUserManagementDetail,
    upload,
    createActor,
    setActorName,
    setAudioName,
    setAudioUrl,
    init,
    setFileUri: (uri: string) => customDigitalHumanManager.setFileUri(uri),
    fileUri: customDigitalHumanManager.getFileUri(),
    reset: () => customAudioManager.reset(),
  }
}

export const customDigitalHumanHooks = {
  useCustomDigitalHuman,
};

export const customDigitalHumanManager = CustomDigitalHumanManager.getInstance();
